import { useState, useMemo } from 'react'
import { Check, X, Clock, ExternalLink, Filter, SortAsc, Copy, Star } from 'lucide-react'
import { DomainResult } from '../types'

interface DomainResultsProps {
  results: DomainResult[]
  searchQuery: string
  isLoading: boolean
}

type FilterType = 'all' | 'available' | 'unavailable' | 'checking'
type SortType = 'relevance' | 'availability' | 'length' | 'tld'

export default function DomainResults({ results, searchQuery, isLoading }: DomainResultsProps) {
  const [filter, setFilter] = useState<FilterType>('all')
  const [sort, setSort] = useState<SortType>('relevance')
  const [favorites, setFavorites] = useState<string[]>([])
  const [copiedDomain, setCopiedDomain] = useState<string | null>(null)

  // Filter and sort results
  const filteredAndSorted = useMemo(() => {
    let filtered = results

    // Apply filter
    switch (filter) {
      case 'available':
        filtered = results.filter(r => r.available === true)
        break
      case 'unavailable':
        filtered = results.filter(r => r.available === false)
        break
      case 'checking':
        filtered = results.filter(r => r.available === null)
        break
    }

    // Apply sort
    return filtered.sort((a, b) => {
      switch (sort) {
        case 'availability':
          if (a.available === b.available) return 0
          if (a.available === true && b.available !== true) return -1
          if (b.available === true && a.available !== true) return 1
          return 0
        case 'length':
          return a.domain.length - b.domain.length
        case 'tld':
          const aTld = a.domain.split('.').pop() || ''
          const bTld = b.domain.split('.').pop() || ''
          return aTld.localeCompare(bTld)
        case 'relevance':
        default:
          // Exact matches first, then starts with, then contains
          const aExact = a.domain.startsWith(searchQuery.toLowerCase())
          const bExact = b.domain.startsWith(searchQuery.toLowerCase())
          if (aExact && !bExact) return -1
          if (bExact && !aExact) return 1
          return a.domain.localeCompare(b.domain)
      }
    })
  }, [results, filter, sort, searchQuery])

  const stats = useMemo(() => {
    const available = results.filter(r => r.available === true).length
    const unavailable = results.filter(r => r.available === false).length
    const checking = results.filter(r => r.available === null).length
    return { available, unavailable, checking, total: results.length }
  }, [results])

  const copyToClipboard = async (domain: string) => {
    try {
      await navigator.clipboard.writeText(domain)
      setCopiedDomain(domain)
      setTimeout(() => setCopiedDomain(null), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const toggleFavorite = (domain: string) => {
    setFavorites(prev => 
      prev.includes(domain) 
        ? prev.filter(d => d !== domain)
        : [...prev, domain]
    )
  }

  const getStatusIcon = (result: DomainResult) => {
    switch (result.status) {
      case 'available':
        return <Check className="w-5 h-5 text-success-600" />
      case 'unavailable':
        return <X className="w-5 h-5 text-danger-600" />
      case 'checking':
        return <Clock className="w-5 h-5 text-warning-600 animate-spin" />
    }
  }

  const getStatusText = (result: DomainResult) => {
    switch (result.status) {
      case 'available':
        return 'Available'
      case 'unavailable':
        return 'Unavailable'
      case 'checking':
        return 'Checking...'
    }
  }

  const getCardClassName = (result: DomainResult) => {
    let baseClass = 'domain-card'
    switch (result.status) {
      case 'available':
        return `${baseClass} domain-available`
      case 'unavailable':
        return `${baseClass} domain-unavailable`
      case 'checking':
        return `${baseClass} domain-checking`
      default:
        return baseClass
    }
  }

  return (
    <div className="max-w-6xl mx-auto mt-8">
      {/* Stats and Controls */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-6 gap-4">
        <div className="flex flex-wrap items-center gap-4">
          <h2 className="text-2xl font-bold text-gray-900">
            Domain Suggestions
          </h2>
          
          <div className="flex items-center space-x-4 text-sm">
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-success-500 rounded-full"></div>
              <span>{stats.available} available</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-danger-500 rounded-full"></div>
              <span>{stats.unavailable} unavailable</span>
            </div>
            {stats.checking > 0 && (
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 bg-warning-500 rounded-full animate-pulse"></div>
                <span>{stats.checking} checking</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {/* Filter */}
          <div className="relative">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as FilterType)}
              className="input pr-8 appearance-none bg-white"
            >
              <option value="all">All ({stats.total})</option>
              <option value="available">Available ({stats.available})</option>
              <option value="unavailable">Unavailable ({stats.unavailable})</option>
              {stats.checking > 0 && (
                <option value="checking">Checking ({stats.checking})</option>
              )}
            </select>
            <Filter className="w-4 h-4 text-gray-400 absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none" />
          </div>

          {/* Sort */}
          <div className="relative">
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as SortType)}
              className="input pr-8 appearance-none bg-white"
            >
              <option value="relevance">Relevance</option>
              <option value="availability">Availability</option>
              <option value="length">Length</option>
              <option value="tld">TLD</option>
            </select>
            <SortAsc className="w-4 h-4 text-gray-400 absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Results Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filteredAndSorted.map(result => (
          <div key={result.domain} className={getCardClassName(result)}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 truncate">
                  {result.domain}
                </h3>
                <div className="flex items-center space-x-2 mt-1">
                  {getStatusIcon(result)}
                  <span className="text-sm font-medium">
                    {getStatusText(result)}
                  </span>
                  {result.confidence && (
                    <span className="text-xs text-gray-500">
                      ({result.confidence})
                    </span>
                  )}
                </div>
              </div>

              <button
                onClick={() => toggleFavorite(result.domain)}
                className="text-gray-400 hover:text-yellow-500 transition-colors"
              >
                <Star 
                  className={`w-5 h-5 ${
                    favorites.includes(result.domain) 
                      ? 'text-yellow-500 fill-current' 
                      : ''
                  }`} 
                />
              </button>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => copyToClipboard(result.domain)}
                className="btn-secondary flex-1 text-sm flex items-center justify-center space-x-1"
              >
                <Copy className="w-4 h-4" />
                <span>
                  {copiedDomain === result.domain ? 'Copied!' : 'Copy'}
                </span>
              </button>

              {result.available && (
                <a
                  href={result.affiliateLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary flex-1 text-sm flex items-center justify-center space-x-1"
                >
                  <span>Buy Now</span>
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </div>

            {result.method && (
              <div className="mt-2 text-xs text-gray-500">
                Checked via {result.method}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Empty state */}
      {filteredAndSorted.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <p className="text-gray-500">
            {filter !== 'all' 
              ? `No ${filter} domains found. Try adjusting your filters.`
              : 'No results found.'
            }
          </p>
        </div>
      )}

      {/* Favorites summary */}
      {favorites.length > 0 && (
        <div className="mt-8 card bg-yellow-50 border-yellow-200">
          <h3 className="font-semibold text-yellow-800 mb-2 flex items-center space-x-2">
            <Star className="w-5 h-5 fill-current" />
            <span>Favorites ({favorites.length})</span>
          </h3>
          <div className="flex flex-wrap gap-2">
            {favorites.map(domain => (
              <span key={domain} className="px-2 py-1 bg-yellow-200 text-yellow-800 rounded text-sm">
                {domain}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
